<template>
  <v-dialog
    v-model="getObserverEditModal"
    width="400"
    content-class="add-head-modal"
  >
    <v-card class="pt-11 pb-13 px-8 rounded-xl elevation-0 bg_sidebar">
      <template v-if="!requestError">
        <p class="mb-8 font-20px dark-color--text">
          {{ forms.id ? $t("Owners.edit_owner") : $t("Settings.add_owner") }}
        </p>

        <div>
          <label class="mb-3 label sub--text">{{ $t("Default.surname") }}</label>
          <v-text-field
            solo
            outlined
            flat
            class="focus-input"
            hide-details="false"
            height="44px"
            v-model="forms.last_name"
          />
        </div>

        <div class="mt-4">
          <label class="mb-3 label sub--text">{{ $t("Default.name") }}</label>
          <v-text-field
            solo
            outlined
            flat
            class="focus-input"
            hide-details="false"
            height="44px"
            v-model="forms.first_name"
          />
        </div>

        <div class="mt-4">
          <label class="mb-3 label sub--text">{{ $t("Default.middle_name") }}</label>
          <v-text-field
            solo
            outlined
            flat
            class="focus-input"
            hide-details="false"
            height="44px"
            v-model="forms.middle_name"
          />
        </div>

        <div class="mt-5">
          <label class="mb-4 label sub--text">{{ $t("Default.number") }}</label>
          <vue-tel-input
            v-if="getObserverEditModal"
            v-model="forms.phone_number"
            class="tel-text-field"
            :enabled-country-code="true"
            :show-dial-code="true"
            default-country="kz"
            :valid-characters-only="true"
            :dropdownOptions="{
              showFlags: true,
              showDialCodeInList: true,
              width: '20rem'
            }"
            :input-options="{showDialCode: true}"
            :allCountries="phoneCountries"
            :preferredCountries="preferredPhoneCountries"
            @validate="onPhoneValidate"
          />
          <div v-if="forms.phone_number && !phoneIsValid" class="error--text font-12px mt-1">
            {{ $t("Reset.phone_error") }}
          </div>
        </div>

        <div class="mt-4">
          <label class="mb-3 label sub--text">Email</label>
          <v-text-field
            solo
            outlined
            flat
            :disabled="!!forms.id"
            class="focus-input"
            hide-details="false"
            height="44px"
            :rules="emailRules"
            v-model="forms.email"
          />
        </div>

        <div class="mt-4">
          <label class="mb-3 label sub--text">{{ $t("Organization.organizations") }}</label>
          <v-checkbox
            v-model="forms.companies"
            v-for="company in companyList"
            :label="company.name"
            :value="company.id"
            class="ma-0 pa-0"
            hide-details
          />
        </div>

        <div class="mt-10">
          <v-btn
            width="100%"
            height="44px"
            elevation="0"
            hide-selected="true"
            class="button"
            color="main_color white--text"
            :disabled="disabled"
            @click="addObserver"
          >
            {{ $t("Default.save") }}
          </v-btn>
          <v-btn
            width="100%"
            height="44px"
            elevation="0"
            hide-selected="true"
            class="button mt-2"
            color="sub white--text"
            @click="getObserverEditModal = false"
          >
            {{ $t("Default.cancel") }}
          </v-btn>
        </div>
      </template>
      <template v-else>
        <p class="mb-8 font-20px dark-color--text text-center">{{ $t("Default.error") }}</p>
        <div>
          <label class="mb-4 label sub--text">{{ requestMessage }}</label>
        </div>
        <div class="mt-10">
          <v-btn
            width="100%"
            height="44px"
            elevation="0"
            hide-selected="true"
            class="button"
            color="main_color white--text"
            @click="closeModal()"
          >
            {{ $t("Default.close") }}
          </v-btn>
        </div>
      </template>
    </v-card>
  </v-dialog>
</template>
<script>
import {computed, ref, watch} from "@vue/composition-api";
import {shortComputed} from "@/utility/helpers";
import {useStore} from "vuex-composition-helpers";
import {globalVariables} from "@/utility/variables";
import {MutationTypes, DispatchTypes} from "@/store/modules/settings/types";
import {checkObjects} from "@/utility/helpers";
import {usePhoneCountries} from "@/composables/usePhoneCountries";

export default {
  name: "getObserverEditModal",
  setup(props, {root}) {
    const {phoneCountries, preferredPhoneCountries} = usePhoneCountries(root);

    const store = useStore();
    const {userInfo} = globalVariables();
    const requestError = ref(false);
    const requestMessage = ref("");
    const forms = ref({
      id: null,
      last_name: "",
      first_name: "",
      middle_name: "",
      phone_number: "",
      email: "",
      companies: [],
    });

    const initialPhoneNumber = ref(null);
    const phoneChanged = computed(() => {
      return (
        initialPhoneNumber.value !== null &&
        forms.value.phone_number !== initialPhoneNumber.value
      );
    });

    const currentObserverTracking = watch(
      () => store.state.settings.getObserverEditModal,
      (isOpen) => {
        if (!isOpen) return;
        const val = store.state.settings.currentObserver;
        forms.value.id = val ? val.id : null;
        forms.value.email = val ? val.email : "";
        forms.value.last_name = val ? val.last_name : "";
        forms.value.first_name = val ? val.first_name : "";
        forms.value.middle_name = val ? val.middle_name : "";
        forms.value.phone_number = val ? val.phone_number : "";
        forms.value.companies = val ? val.companies.map((item) => item.id) : [];
        initialPhoneNumber.value = forms.value.phone_number;
        requestError.value = false;
      }
    );

    const getObserverEditModal = shortComputed(
      {
        storeType: "settings",
        key: "getObserverEditModal",
      },
      MutationTypes.setObserverEditModal
    );

    const emailRules = ref([
      (v) => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || "",
    ]);

    const companyList = computed(() => store.state.companies.companies.results);

    const phoneIsValid = ref(true);
    const onPhoneValidate = (payload) => {
      phoneIsValid.value = Boolean(
        payload && (typeof payload.valid === "boolean" ? payload.valid : payload.isValid === true)
      );
    };

    const disabled = computed(() => {
      return !(
        emailRules.value[0](forms.value.email) &&
        (!forms.value.phone_number || phoneIsValid.value || !phoneChanged.value) &&
        checkObjects(forms.value.email && forms.value.first_name && forms.value.last_name) &&
        forms.value.companies.length > 0
      );
    });

    const addObserver = () => {
      let type = forms.value.id ? DispatchTypes.editObserver : DispatchTypes.addObserver
      store.dispatch(type, forms.value).then((res) => {
        store.commit(MutationTypes.setObserverEditModal, false);
      }).catch((err) => {
        requestError.value = true
        requestMessage.value = err.message
      });
    };

    const closeModal = () => {
      getObserverEditModal.value = false
      requestError.value = false
    }

    return {
      store,
      getObserverEditModal,
      userInfo,
      addObserver,
      companyList,
      disabled,
      emailRules,
      requestError,
      requestMessage,
      closeModal,
      forms,
      currentObserverTracking,
      phoneIsValid,
      onPhoneValidate,
      phoneCountries,
      preferredPhoneCountries,
    };
  },
};
</script>
